import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/solid";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { Button } from "../../Components/Button";
import { cancelAPIRequest, classNames, getErrorMessages } from "../../Utils";
import { ErrorList } from "../../Components/ErrorList";
import { EmptyList } from "../../Components/EmptyList";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clearBusinessPartner,
  clearBusinessPartnerError,
  requestAddMainBusiness,
  requestDeleteBusinessPartner,
  requestListBusiness,
  requestListBusinessPartner,
  requestUpdateBusinessPartner,
  useAuthDispatch,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import { Pagination } from "../../Components/Pagination";
import TableSkeleton from "../../Components/TableSkeleton";
import { PencilAltIcon, TrashIcon } from "@heroicons/react/outline";
import { Modal } from "../../Components/Modal";
import { NumericFormat } from "react-number-format";
import FormPhoneInput from "../../Components/v2/Form/FormPhoneInput";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { ModalAlert } from "../../Components/ModalAlert";
import FormLabel from "../../Components/FormLabel";
import {
  BUSINESS_TYPES_OPTIONS,
  isBusinessMainBlocked,
} from "../../Constants/business";

const BUSINESS_TYPE = {
  PERSONAL: "Personal",
  BUSINESS: "Business",
};

export const Partner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthState();

  const [selectedBusiness, setselectedBusiness] = useState({});
  const [page, setpage] = useState(1);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [partnerName, setpartnerName] = useState("");
  const [partnerId, setpartnerId] = useState("");
  const [email, setemail] = useState("");
  const [type, settype] = useState("PERSONAL");
  const [phone, setphone] = useState();
  const [npwp, setnpwp] = useState("");
  const [address, setaddress] = useState("");
  const [emailError, setemailError] = useState(null);
  const [phoneError, setphoneError] = useState(null);
  const [nameError, setnameError] = useState(null);
  const [npwpError, setnpwpError] = useState(null);

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const cleanedPhone = phone?.replace(/\s/g, "");

  const businessTransactionDispatch = useBusinessTransactionDispatch();
  const authDispatch = useAuthDispatch();

  const {
    request,
    businesses,
    businessPartners,
    businessPartnerNextTokens,
    businessPartnerLoading,
    businessPartnerError,
    updatePartnerError,
    updatePartnerLoading,
    partnerID,
    deletePartnerLoading,
    deletePartnerError,
    deletedID,
  } = useBusinessTransactionState();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const businessIdParam = searchParams.get("id");

  useEffect(() => {
    if (user.id) {
      requestListBusiness(businessTransactionDispatch, {
        owner: user.id,
        page: 1,
        limit: 10,
      });
    }
  }, [businessTransactionDispatch, user.id]);

  useEffect(() => {
    // Function to set selected business
    const setSelectedBusinessLogic = async () => {
      if (businessIdParam) {
        if (businesses.length > 0) {
          const business = businesses.find(
            (item) => item.value === businessIdParam
          );
          if (business) {
            setselectedBusiness({
              ...business,
              label: business.name,
              value: business.id,
            });
          } else {
            // Handle if business is not found, select index 0
            setselectedBusiness({
              ...businesses[0],
              label: businesses[0].name,
              value: businesses[0].id,
            });
          }
        } else {
          // Handle if businesses are empty, no business to select
        }
      } else if (user.mainBusiness) {
        // Set selectedBusiness directly from user.mainBusiness
        setselectedBusiness({
          ...user.mainBusiness,
          label: user.mainBusiness.name,
          value: user.mainBusiness.id,
        });
      } else if (businesses.length > 0) {
        // Set selected business to the first item in the list
        // Prefer a healthy business: a RESTRICTED / PENDING_DELETION one must
        // not become the account-wide main business. Falls back to the first
        // entry when every business is blocked.
        const mainCandidate =
          businesses.find((business) => !isBusinessMainBlocked(business)) ||
          businesses[0];
        setselectedBusiness({
          ...mainCandidate,
          label: mainCandidate.name,
          value: mainCandidate.id,
        });
        requestAddMainBusiness(authDispatch, {
          businessID: mainCandidate.id,
        });
      } else {
        // Handle if businesses are empty, no business to select
      }
    };

    setSelectedBusinessLogic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businesses, businessIdParam, user.mainBusiness]);

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessTransactionDispatch, selectedBusiness.value]);

  useEffect(() => {
    if (partnerID) {
      setShowEditModal(false);
      setShowSuccessModal(true);
      setShowConfirmModal(false);
    } else if (updatePartnerError || deletePartnerError) {
      setShowEditModal(false);
      setShowErrorModal(true);
      setShowConfirmModal(false);
    }
  }, [partnerID, updatePartnerError, deletePartnerError]);

  useEffect(() => {
    if (!deletedID) {
      return;
    }
    setShowConfirmModal(false);
    setShowSuccessModal(true);
  }, [deletedID]);

  useEffect(() => {
    const isNameValid = partnerName.length >= 2;
    if (isNameValid) {
      setErrorMessage("name", "");
    } else {
      setErrorMessage(
        "name",
        "Partner name must be at least 2 characters long."
      );
    }
  }, [partnerName]);

  useEffect(() => {
    if (phone) {
      const isPhoneValid = cleanedPhone?.length >= 10;
      if (isPhoneValid) {
        setErrorMessage("phone", "");
      } else {
        setErrorMessage("phone", "Please enter a valid phone number.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  useEffect(() => {
    if (email) {
      const isEmailValid = emailRegex.test(email);
      if (isEmailValid) {
        setErrorMessage("email", "");
      } else {
        setErrorMessage("email", "Please enter a valid email address.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    if (npwp) {
      const isNpwpValid = npwp.length >= 15 && npwp.length <= 16;
      if (isNpwpValid) {
        setErrorMessage("npwp", "");
      } else {
        setErrorMessage("npwp", "Invalid NPWP. Enter 15-16 digits.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npwp]);

  const setErrorMessage = (field, message) => {
    switch (field) {
      case "email":
        setemailError(message);
        break;
      case "phone":
        setphoneError(message);
        break;
      case "name":
        setnameError(message);
        break;
      case "npwp":
        setnpwpError(message);
        break;
      default:
        break;
    }
  };

  const onLoad = () => {
    if (request) {
      cancelAPIRequest(request);
    }
    if (selectedBusiness.value) {
      requestListBusinessPartner(businessTransactionDispatch, {
        page: 1,
        businessID: selectedBusiness.value,
        limit: 10,
      });
    }
  };

  const validateInput = () => {
    let currentValidName = true;
    let currentValidPhone = true;
    let currentValidEmail = true;
    let currentValidNpwp = true;

    const isEmailValid = emailRegex.test(email);
    const isPhoneValid = cleanedPhone?.length >= 10;
    const isNameValid = partnerName.length >= 2;
    const isNpwpValid = npwp?.length >= 15 && npwp?.length <= 16;

    currentValidName = isNameValid;

    if (phone) {
      currentValidPhone = isPhoneValid;
    }

    if (email) {
      currentValidEmail = isEmailValid;
    }

    if (npwp) {
      currentValidEmail = isNpwpValid;
    }

    return (
      currentValidName &&
      currentValidPhone &&
      currentValidEmail &&
      currentValidNpwp
    );
  };

  const clearInputErrorMessages = () => {
    setErrorMessage("name", "");
    setErrorMessage("phone", "");
    setErrorMessage("email", "");
    setErrorMessage("npwp", "");
  };

  const onCreate = () => {
    clearBusinessPartnerError(businessTransactionDispatch);
    navigate(`/partner/create?id=${selectedBusiness.value}`);
  };

  const onUpdate = () => {
    const validForm = validateInput();

    if (validForm) {
      const input = {
        id: partnerId,
        name: partnerName,
        email,
        phone,
        type: type?.value || null,
        taxNumber: npwp,
        address,
      };

      requestUpdateBusinessPartner(businessTransactionDispatch, { input });
    }
  };

  const onDelete = () => {
    requestDeleteBusinessPartner(businessTransactionDispatch, {
      id: partnerId,
    });
  };

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (businessPartnerNextTokens[nextPage]) {
      requestListBusinessPartner(businessTransactionDispatch, {
        page: nextPage,
        limit: 10,
        businessID: selectedBusiness.value,
        nextTokens: businessPartnerNextTokens[nextPage],
      });
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      requestListBusinessPartner(businessTransactionDispatch, {
        page: prevPage,
        businessID: selectedBusiness.value,
        nextTokens: businessPartnerNextTokens[prevPage],
        limit: 10,
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (businessPartnerNextTokens[selectedPage] || selectedPage === 1) {
      requestListBusinessPartner(businessTransactionDispatch, {
        page: selectedPage,
        businessID: selectedBusiness.value,
        nextTokens: businessPartnerNextTokens[selectedPage],
        limit: 10,
      });
    }
  };

  const resetErrors = () => {
    setnameError("");
    setemailError("");
    setphoneError("");
    setnpwpError("");
  };

  return (
    <div className="space-y-4">
      {(updatePartnerError || deletePartnerError) && (
        <ModalConfirm
          open={showErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setShowErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(
            updatePartnerError || deletePartnerError
          )}
        />
      )}
      <ModalAlert
        open={showConfirmModal}
        setOpen={(open) => {
          setShowConfirmModal(open);
        }}
        title="Delete Partner"
        description="Contacts can only be deleted if they have not been used in any transaction. Continue?"
        confirmButtonText="Yes"
        cancelButtonText="Cancel"
        loadingButton={deletePartnerLoading}
        disabledButton={deletePartnerLoading}
        onConfirm={onDelete}
      />
      <ModalConfirm
        open={showSuccessModal}
        type="SUCCESS"
        setOpen={(open) => {
          setShowSuccessModal(open);
        }}
        title="Success"
        description={
          deletedID
            ? "The partner has been successfully deleted"
            : "The partner profile has been successfully changed"
        }
        onConfirm={() => clearBusinessPartner(businessTransactionDispatch)}
      />
      <Modal
        show={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          resetErrors();
          clearInputErrorMessages();
        }}
        title={t("Change Partner Profile")}
        footer={
          <div className="p-4 flex justify-end items-center bg-gray-50 rounded-b-lg">
            <Button
              type="submit"
              disabled={updatePartnerLoading}
              loading={updatePartnerLoading}
              title="Save"
              onClick={onUpdate}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4 p-4 text-sm text-gray-700">
          <div>
            <FormLabel htmlFor="business" label={t("Business Name")} required />
            <input
              type="text"
              name="business"
              value={selectedBusiness.label}
              disabled
              className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>
          <div>
            <FormLabel htmlFor="name" label={t("Name")} required />
            <input
              type="text"
              name="name"
              value={partnerName}
              placeholder={t("Enter Partner Name")}
              onChange={(e) => setpartnerName(e.target.value)}
              className={classNames(
                nameError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm"
              )}
            />
            {nameError && (
              <div className="mt-1 text-red-600 text-sm">{t(nameError)}</div>
            )}
          </div>
          <div>
            <FormLabel htmlFor="type" label={t("Business Type")} />
            <Select
              id="type"
              name="type"
              className="mt-1 text-sm"
              classNamePrefix="select2-selection"
              options={BUSINESS_TYPES_OPTIONS}
              placeholder={t("Business Type")}
              value={type}
              onChange={(value) => {
                settype(value);
              }}
              isClearable
            />
          </div>
          <div>
            <FormLabel
              htmlFor="npwp"
              label={t("Tax Identification Number (NPWP)")}
            />
            <NumericFormat
              type="text"
              name="npwp"
              id="npwp"
              allowLeadingZeros
              className={classNames(
                npwpError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm"
              )}
              placeholder={t("Enter the tax identification number (NPWP)")}
              displayType="input"
              value={npwp || ""}
              onValueChange={(value) => {
                setnpwp(value.value);
              }}
              isAllowed={({ value }) => value.length <= 16}
            />
            {npwpError && (
              <div className="mt-1 text-red-600 text-sm">{t(npwpError)}</div>
            )}
          </div>
          <div>
            <FormLabel htmlFor="email" label={t("Email")} />
            <input
              type="email"
              name="email"
              value={email}
              placeholder={t("Enter email")}
              onChange={(e) => setemail(e.target.value)}
              className={classNames(
                emailError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm"
              )}
            />
            {emailError && (
              <div className="mt-1 text-red-600 text-sm">{t(emailError)}</div>
            )}
          </div>
          <div>
            <FormLabel htmlFor="phone" label={t("Phone Number")} />
            <FormPhoneInput
              id="phone"
              name="phone"
              value={phone}
              onChange={setphone}
              placeholder={t("Enter Phone Number")}
              className={classNames(
                phoneError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm"
              )}
            />
            {phoneError && (
              <div className="mt-1 text-red-600 text-sm">{t(phoneError)}</div>
            )}
          </div>
          <div>
            <FormLabel htmlFor="address" label={t("Address")} />
            <input
              id="address"
              type="text"
              name="address"
              value={address}
              onChange={(e) => setaddress(e.target.value)}
              placeholder={t("Enter Address")}
              className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>
      </Modal>
      <div className="grid gird-cols-1 sm:grid-cols-2 gap-y-4 mb-4">
        <div className="max-w-md md:max-w-2xl">
          <Listbox value={selectedBusiness} onChange={setselectedBusiness}>
            {({ open }) => (
              <>
                <ListboxButton className="flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm sm:text-sm">
                  <span className="max-w-full ml-3 mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                    {selectedBusiness?.label || t("Business Name")}
                  </span>
                  <SelectorIcon className="w-5 h-5 text-gray-500" />
                </ListboxButton>
                <Transition
                  show={open}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 sm:w-auto rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {businesses.map((business) => (
                      <ListboxOption
                        key={business.value}
                        className={({ focus }) =>
                          classNames(
                            focus ? "text-white bg-blue-600" : "text-gray-900",
                            "cursor-default select-none relative py-2 pl-3 pr-9"
                          )
                        }
                        value={business}
                      >
                        {({ selected, focus }) => (
                          <>
                            <div className="flex items-center justify-between">
                              <span
                                className={classNames(
                                  selected ? "font-semibold" : "font-normal",
                                  "ml-3 mr-2 block truncate"
                                )}
                              >
                                {business.label}
                              </span>
                              {selected ? (
                                <span
                                  className={classNames(
                                    focus ? "text-white" : "text-blue-600",
                                    "absolute inset-y-0 right-0 flex items-center pr-4"
                                  )}
                                >
                                  <CheckIcon className="h-5 w-5" />
                                </span>
                              ) : null}
                            </div>
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </>
            )}
          </Listbox>
        </div>
        <div className="gap-4 flex justify-start sm:justify-end w-full">
          <Button
            className="w-full sm:w-auto"
            type="button"
            onClick={onCreate}
            title={t("Create New Partner")}
          />
        </div>
      </div>
      <div className="flex flex-col">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Name")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Email")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Phone")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Business Type")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Action")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap" />
                  </tr>
                </thead>
                {businessPartnerLoading ? (
                  <TableSkeleton rows={10} columns={6} />
                ) : (
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {businessPartners.length > 0 ? (
                      businessPartners.map((partner) => {
                        const titleCaseType =
                          BUSINESS_TYPE[partner.type] || partner.type;

                        return (
                          <tr key={partner.id}>
                            <td className="px-4 py-2 font-medium text-sm text-gray-00 whitespace-nowrap ">
                              {partner.name}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              {t(partner.email)}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              {t(partner.phone)}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              {t(titleCaseType)}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              <div className="flex flex-auto gap-2">
                                <button
                                  onClick={() => {
                                    setShowEditModal(true);
                                    const selectedType =
                                      BUSINESS_TYPES_OPTIONS.find(
                                        (item) => item.value === partner.type
                                      );
                                    setpartnerId(partner.id);
                                    setpartnerName(partner.name);
                                    setemail(partner.email);
                                    setphone(partner.phone);
                                    settype(selectedType);
                                    setnpwp(partner.taxNumber);
                                    setaddress(partner.address);
                                  }}
                                >
                                  <PencilAltIcon className="h-5 w-5 text-blue-500" />
                                </button>
                                <button
                                  onClick={() => {
                                    setShowConfirmModal(true);
                                    setpartnerId(partner.id);
                                  }}
                                >
                                  <TrashIcon className="h-5 w-5 text-red-500" />
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-sm font-medium text-left text-gray-700 whitespace-nowrap">
                              <button
                                onClick={() =>
                                  navigate(`/partner/${partner.id}`)
                                }
                                className="px-4 py-2 text-sm text-left text-blue-500 whitespace-nowrap"
                              >
                                {t("Transaction")}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td className="p-4" colSpan={6}>
                          {businessPartnerError ? (
                            <ErrorList
                              message={
                                businessPartnerError?.message ||
                                "Error fetching partners."
                              }
                              errors={businessPartnerError?.errors}
                            />
                          ) : (
                            <EmptyList title="No Partner" />
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}
              </table>
              <Pagination
                page={page}
                onPrev={onPrev}
                onNext={onNext}
                onPageChange={onPageChange}
                nextTokens={businessPartnerNextTokens}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
